import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    Typography,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
// import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
// import FilterListIcon from '@mui/icons-material/FilterList';
import CashIcon from '@mui/icons-material/LocalAtm';
import OnlineIcon from '@mui/icons-material/Language';
import MobileIcon from '@mui/icons-material/PhoneAndroid';
import CardIcon from '@mui/icons-material/CreditCard';
import TicketIcon from '@mui/icons-material/ConfirmationNumber';
import BadgeIcon from '@mui/icons-material/Badge';

// ─── Stat Cards ───────────────────────────────────────────────────────────────

const statCards = [
    {
        label: 'Total Revenue (MTD)',
        value: 'Rs. 124,560,850',
        sub: '▲ 12.5% vs Previous Period',
        up: true,
        color: '#1976d2',
        bg: '#e3f2fd',
        icon: <AttachMoneyIcon sx={{ fontSize: 22, color: '#1976d2' }} />,
    },
    {
        label: 'Total Transactions (MTD)',
        value: '2,356,780',
        sub: '▲ 9.7% vs Previous Period',
        up: true,
        color: '#2e7d32',
        bg: '#e8f5e9',
        icon: <ReceiptLongIcon sx={{ fontSize: 22, color: '#2e7d32' }} />,
    },
    {
        label: 'Non-Cash Revenue %',
        value: '72.4%',
        sub: '▲ 5.8% vs Previous Period',
        up: true,
        color: '#5c35a0',
        bg: '#ede7f6',
        icon: <CreditScoreIcon sx={{ fontSize: 22, color: '#5c35a0' }} />,
    },
    {
        label: 'Avg Revenue per Transaction',
        value: 'Rs. 52.88',
        sub: '▲ 2.6% vs Previous Period',
        up: true,
        color: '#ed6c02',
        bg: '#fff3e0',
        icon: <PointOfSaleIcon sx={{ fontSize: 22, color: '#ed6c02' }} />,
    },
    {
        label: 'Digital Growth % (MTD)',
        value: '13.2%',
        sub: '▲ 4.3% vs Previous Period',
        up: true,
        color: '#d32f2f',
        bg: '#ffebee',
        icon: <TrendingUpOutlinedIcon sx={{ fontSize: 22, color: '#d32f2f' }} />,
    },
];

// ─── Channel Snapshot Data ────────────────────────────────────────────────────

const channelSnapshotData = [
    {
        name: 'Cash (Onboard)',
        icon: <CashIcon sx={{ fontSize: 18, color: '#1976d2' }} />,
        iconBg: '#e3f2fd',
        transactions: 1025340,
        vsTransPrev: 6.2,
        vsTransUp: true,
        revenue: 31245600,
        vsRevPrev: 3.8,
        vsRevUp: true,
        growth: 6.2,
        growthUp: true,
        share: 25.1,
        avgRevPerTx: 30.47,
        status: 'Stable',
        statusColor: 'default',
    },
    {
        name: 'Online Booking',
        icon: <OnlineIcon sx={{ fontSize: 18, color: '#2e7d32' }} />,
        iconBg: '#e8f5e9',
        transactions: 568420,
        vsTransPrev: 14.5,
        vsTransUp: true,
        revenue: 34568200,
        vsRevPrev: 16.3,
        vsRevUp: true,
        growth: 14.5,
        growthUp: true,
        share: 27.7,
        avgRevPerTx: 60.87,
        status: 'Growing',
        statusColor: 'success',
    },
    {
        name: 'Online Onboard',
        icon: <MobileIcon sx={{ fontSize: 18, color: '#5c35a0' }} />,
        iconBg: '#ede7f6',
        transactions: 342180,
        vsTransPrev: 21.7,
        vsTransUp: true,
        revenue: 21456780,
        vsRevPrev: 19.8,
        vsRevUp: true,
        growth: 21.7,
        growthUp: true,
        share: 17.2,
        avgRevPerTx: 62.74,
        status: 'Growing',
        statusColor: 'success',
    },
    {
        name: 'Travel Card',
        icon: <CardIcon sx={{ fontSize: 18, color: '#ed6c02' }} />,
        iconBg: '#fff3e0',
        transactions: 258760,
        vsTransPrev: 10.3,
        vsTransUp: true,
        revenue: 14569120,
        vsRevPrev: 8.9,
        vsRevUp: true,
        growth: 10.3,
        growthUp: true,
        share: 11.7,
        avgRevPerTx: 56.26,
        status: 'Stable',
        statusColor: 'default',
    },
    {
        name: 'Season Tickets',
        icon: <TicketIcon sx={{ fontSize: 18, color: '#00acc1' }} />,
        iconBg: '#e0f7fa',
        transactions: 112430,
        vsTransPrev: 5.1,
        vsTransUp: true,
        revenue: 13245300,
        vsRevPrev: 4.2,
        vsRevUp: true,
        growth: 5.1,
        growthUp: true,
        share: 10.6,
        avgRevPerTx: 117.78,
        status: 'Stable',
        statusColor: 'default',
    },
    {
        name: 'Govt Pass',
        icon: <BadgeIcon sx={{ fontSize: 18, color: '#78909c' }} />,
        iconBg: '#eceff1',
        transactions: 49650,
        vsTransPrev: 2.3,
        vsTransUp: false,
        revenue: 9475850,
        vsRevPrev: 1.1,
        vsRevUp: false,
        growth: -2.3,
        growthUp: false,
        share: 7.6,
        avgRevPerTx: 190.99,
        status: 'Declining',
        statusColor: 'error',
    },
];

// ─── Channel Performance Details (daily rows) ─────────────────────────────────

const detailRows = [
    { date: '01 May 2026', cash: 1025400, online: 1145600, onboard: 725300, travel: 489600, season: 441200, govt: 315400, totalRev: 4142500, totalTx: 78450, nonCash: 75.2 },
    { date: '02 May 2026', cash: 1058700, online: 1205800, onboard: 742100, travel: 502300, season: 452600, govt: 329100, totalRev: 4290600, totalTx: 80120, nonCash: 75.4 },
    { date: '03 May 2026', cash: 1012300, online: 1182400, onboard: 698700, travel: 476800, season: 430900, govt: 301200, totalRev: 4102300, totalTx: 76540, nonCash: 73.9 },
    { date: '04 May 2026', cash: 1085600, online: 1265700, onboard: 765400, travel: 512900, season: 460300, govt: 335800, totalRev: 4425700, totalTx: 82430, nonCash: 75.5 },
    { date: '05 May 2026', cash: 1125300, online: 1345200, onboard: 812500, travel: 528600, season: 478700, govt: 348600, totalRev: 4638900, totalTx: 85130, nonCash: 75.8 },
    { date: '06 May 2026', cash: 1152200, online: 1398400, onboard: 845800, travel: 536700, season: 485600, govt: 357200, totalRev: 4775900, totalTx: 87450, nonCash: 76.2 },
    { date: '07 May 2026', cash: 1147100, online: 1425100, onboard: 864980, travel: 542300, season: 495000, govt: 366600, totalRev: 4841080, totalTx: 88650, nonCash: 76.1 },
    { date: '08 May 2026', cash: 1098400, online: 1367890, onboard: 823450, travel: 498600, season: 456780, govt: 312400, totalRev: 4557520, totalTx: 84320, nonCash: 75.9 },
    { date: '09 May 2026', cash: 1189600, online: 1456780, onboard: 876540, travel: 523400, season: 456780, govt: 317200, totalRev: 4820300, totalTx: 89120, nonCash: 75.3 },
    { date: '10 May 2026', cash: 1198400, online: 1367890, onboard: 823450, travel: 498600, season: 423400, govt: 298910, totalRev: 4610650, totalTx: 85340, nonCash: 74.0 },
    { date: '11 May 2026', cash: 1256700, online: 1523400, onboard: 912300, travel: 567800, season: 478900, govt: 321100, totalRev: 5060200, totalTx: 93410, nonCash: 75.2 },
    { date: '12 May 2026', cash: 1367800, online: 1789200, onboard: 1045600, travel: 623400, season: 534500, govt: 359950, totalRev: 5720450, totalTx: 105620, nonCash: 76.1 },
    { date: '13 May 2026', cash: 1256700, online: 1678900, onboard: 989300, travel: 589600, season: 512300, govt: 343980, totalRev: 5370780, totalTx: 99230, nonCash: 76.6 },
    { date: '14 May 2026', cash: 1189600, online: 1612300, onboard: 945600, travel: 567800, season: 489400, govt: 335640, totalRev: 5140340, totalTx: 94890, nonCash: 76.9 },
    { date: '15 May 2026', cash: 1423400, online: 1845600, onboard: 1087600, travel: 645600, season: 567800, govt: 380890, totalRev: 5950890, totalTx: 109870, nonCash: 76.1 },
    { date: '16 May 2026', cash: 1356700, online: 1756800, onboard: 1034500, travel: 623400, season: 545600, govt: 363120, totalRev: 5680120, totalTx: 104880, nonCash: 76.1 },
    { date: '17 May 2026', cash: 1489300, online: 1923400, onboard: 1145600, travel: 678900, season: 589600, govt: 403650, totalRev: 6230450, totalTx: 115010, nonCash: 76.1 },
    { date: '18 May 2026', cash: 1545600, online: 1989300, onboard: 1178900, travel: 698900, season: 612300, govt: 415450, totalRev: 6440450, totalTx: 118870, nonCash: 76.0 },
    { date: '19 May 2026', cash: 1423400, online: 1856700, onboard: 1089300, travel: 656700, season: 578900, govt: 385230, totalRev: 5990230, totalTx: 110560, nonCash: 76.2 },
    { date: '20 May 2026', cash: 1256700, online: 1678900, onboard: 989300, travel: 589600, season: 512300, govt: 343870, totalRev: 5370670, totalTx: 99230, nonCash: 76.6 },
    { date: '21 May 2026', cash: 1189600, online: 1456780, onboard: 876540, travel: 523400, season: 456780, govt: 317240, totalRev: 4820340, totalTx: 89120, nonCash: 75.3 },
    { date: '22 May 2026', cash: 1298700, online: 1612300, onboard: 945600, travel: 567800, season: 489400, govt: 357090, totalRev: 5270890, totalTx: 97340, nonCash: 75.4 },
    { date: '23 May 2026', cash: 1356700, online: 1756800, onboard: 1034500, travel: 623400, season: 545600, govt: 363450, totalRev: 5680450, totalTx: 104880, nonCash: 76.1 },
    { date: '24 May 2026', cash: 1489300, online: 1923400, onboard: 1145600, travel: 678900, season: 589600, govt: 403320, totalRev: 6230120, totalTx: 115010, nonCash: 76.1 },
    { date: '25 May 2026', cash: 1545600, online: 1989300, onboard: 1178900, travel: 698900, season: 612300, govt: 415450, totalRev: 6440450, totalTx: 118870, nonCash: 76.0 },
    { date: '26 May 2026', cash: 1423400, online: 1856700, onboard: 1089300, travel: 656700, season: 578900, govt: 385230, totalRev: 5990230, totalTx: 110560, nonCash: 76.2 },
    { date: '27 May 2026', cash: 1256700, online: 1678900, onboard: 989300, travel: 589600, season: 512300, govt: 343650, totalRev: 5370450, totalTx: 99230, nonCash: 76.6 },
    { date: '28 May 2026', cash: 1189600, online: 1456780, onboard: 876540, travel: 523400, season: 456780, govt: 327020, totalRev: 4830120, totalTx: 89120, nonCash: 75.4 },
    { date: '29 May 2026', cash: 1098400, online: 1367890, onboard: 823450, travel: 498600, season: 423400, govt: 278930, totalRev: 4490670, totalTx: 82930, nonCash: 75.5 },
    { date: '30 May 2026', cash: 1025450, online: 1345600, onboard: 745230, travel: 489650, season: 367800, govt: 236610, totalRev: 4210340, totalTx: 77830, nonCash: 75.6 },
    { date: '31 May 2026', cash: 945600, online: 1256780, onboard: 689450, travel: 436200, season: 346780, govt: 210080, totalRev: 3884890, totalTx: 71750, nonCash: 75.7 },
];

// Totals for details footer
const detailTotals = {
    cash: detailRows.reduce((s, r) => s + r.cash, 0),
    online: detailRows.reduce((s, r) => s + r.online, 0),
    onboard: detailRows.reduce((s, r) => s + r.onboard, 0),
    travel: detailRows.reduce((s, r) => s + r.travel, 0),
    season: detailRows.reduce((s, r) => s + r.season, 0),
    govt: detailRows.reduce((s, r) => s + r.govt, 0),
    totalRev: detailRows.reduce((s, r) => s + r.totalRev, 0),
    totalTx: detailRows.reduce((s, r) => s + r.totalTx, 0),
};

const statusChip = (status) => {
    const map = {
        Stable: { color: '#1976d2', bg: '#e3f2fd' },
        Growing: { color: '#2e7d32', bg: '#e8f5e9' },
        Declining: { color: '#d32f2f', bg: '#ffebee' },
    };
    const s = map[status] || { color: '#888', bg: '#f5f5f5' };
    return (
        <Box sx={{
            display: 'inline-block',
            px: 1.2, py: 0.3,
            borderRadius: 1,
            backgroundColor: s.bg,
            color: s.color,
            fontWeight: 600,
            fontSize: 11,
            border: `1px solid ${s.color}33`,
        }}>
            {status}
        </Box>
    );
};

const ChannelPerformancePage = () => {
    const [depot, setDepot] = useState('');
    const [route, setRoute] = useState('');
    const [channel, setChannel] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };
    const startIndex = page * rowsPerPage;

    const thSx = { py: 1, fontSize: 12, fontWeight: 600 };
    const tdSx = { py: 0.5, fontSize: 12 };

    return (
        <Container component="main" maxWidth={false} sx={{ py: 0, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>

                {/* ── Page Header ── */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Channel Performance</Typography>
                </Box>

                {/* ── Stat Cards (5 across) ── */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(5,1fr)' },
                    gap: 2,
                    width: '100%',
                    mb: 3,
                }}>
                    {statCards.map((card) => (
                        <Paper key={card.label} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box sx={{ backgroundColor: card.bg, borderRadius: 1.5, p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    {card.icon}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3 }}>
                                    {card.label}
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: card.color, lineHeight: 1.2, mb: 0.5, fontSize: { xs: 16, md: 14, lg: 16 } }}>
                                {card.value}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {card.up
                                    ? <TrendingUpIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                                    : <TrendingDownIcon sx={{ fontSize: 13, color: '#d32f2f' }} />}
                                <Typography variant="caption" sx={{ color: card.up ? '#2e7d32' : '#d32f2f', fontWeight: 600, fontSize: 11 }}>
                                    {card.sub}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {/* ── Filter Bar ── */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr) auto auto' },
                    gap: 2,
                    width: '100%',
                    mb: 3,
                    alignItems: 'center',
                }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Date Range</InputLabel>
                        <Select value="may2026" label="Date Range">
                            <MenuItem value="may2026">01 May 2026 – 31 May 2026</MenuItem>
                            <MenuItem value="apr2026">01 Apr 2026 – 30 Apr 2026</MenuItem>
                            <MenuItem value="mar2026">01 Mar 2026 – 31 Mar 2026</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Depot</InputLabel>
                        <Select value={depot} onChange={e => setDepot(e.target.value)} label="Depot">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="colombo">Colombo Depot</MenuItem>
                            <MenuItem value="kandy">Kandy Depot</MenuItem>
                            <MenuItem value="kurunegala">Kurunegala Depot</MenuItem>
                            <MenuItem value="matara">Matara Depot</MenuItem>
                            <MenuItem value="galle">Galle Depot</MenuItem>
                            <MenuItem value="negombo">Negombo Depot</MenuItem>
                            <MenuItem value="jaffna">Jaffna Depot</MenuItem>
                            <MenuItem value="batticaloa">Batticaloa Depot</MenuItem>
                            <MenuItem value="anuradhapura">Anuradhapura Depot</MenuItem>
                            <MenuItem value="puttalam">Puttalam Depot</MenuItem>
                            <MenuItem value="hambantota">Hambantota Depot</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Route</InputLabel>
                        <Select value={route} onChange={e => setRoute(e.target.value)} label="Route">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="R-001">R-001 – Colombo – Kandy</MenuItem>
                            <MenuItem value="R-017">R-017 – Kandy – Kurunegala</MenuItem>
                            <MenuItem value="R-025">R-025 – Kurunegala – Puttalam</MenuItem>
                            <MenuItem value="R-003">R-003 – Kandy – Matale</MenuItem>
                            <MenuItem value="R-042">R-042 – Colombo – Negombo</MenuItem>
                            <MenuItem value="R-085">R-085 – Matara – Akuressa</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Channel</InputLabel>
                        <Select value={channel} onChange={e => setChannel(e.target.value)} label="Channel">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="cash">Cash (Onboard)</MenuItem>
                            <MenuItem value="online">Online Booking</MenuItem>
                            <MenuItem value="onboard">Online Onboard</MenuItem>
                            <MenuItem value="travel">Travel Card</MenuItem>
                            <MenuItem value="season">Season Tickets</MenuItem>
                            <MenuItem value="govt">Govt Pass</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 'bold', height: 40, backgroundColor: '#3f51b5', '&:hover': { backgroundColor: '#303f9f' }, whiteSpace: 'nowrap', px: 3 }}
                    >
                        Apply
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, height: 40, whiteSpace: 'nowrap', px: 3 }}
                    >
                        Reset
                    </Button>
                </Box>

                {/* ══════════════════════════════════════════
                    TABLE 1 — Channel Snapshot (MTD)
                ══════════════════════════════════════════ */}
                <Box sx={{ width: '100%', mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Channel Snapshot (MTD)</Typography>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', borderRadius: 2, mb: 4 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                <TableCell sx={thSx}>Channel</TableCell>
                                <TableCell sx={thSx} align="right">Transactions</TableCell>
                                <TableCell sx={thSx} align="right">vs Previous Period</TableCell>
                                <TableCell sx={thSx} align="right">Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">vs Previous Period</TableCell>
                                <TableCell sx={thSx} align="right">Growth % (Transactions)</TableCell>
                                <TableCell sx={thSx} align="right">Share % (Revenue)</TableCell>
                                <TableCell sx={thSx} align="right">Avg Revenue per Transaction (Rs.)</TableCell>
                                <TableCell sx={thSx} align="center">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {channelSnapshotData.map((row) => (
                                <TableRow key={row.name} hover>
                                    <TableCell sx={{ ...tdSx, fontWeight: 600 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ backgroundColor: row.iconBg, borderRadius: 1, p: 0.4, display: 'flex', alignItems: 'center' }}>
                                                {row.icon}
                                            </Box>
                                            {row.name}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tdSx} align="right">{row.transactions.toLocaleString()}</TableCell>
                                    <TableCell sx={tdSx} align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                                            {row.vsTransUp
                                                ? <TrendingUpIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                                                : <TrendingDownIcon sx={{ fontSize: 13, color: '#d32f2f' }} />}
                                            <Typography variant="caption" sx={{ color: row.vsTransUp ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
                                                {row.vsTransUp ? '▲' : '▼'} {row.vsTransPrev}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...tdSx, fontWeight: 600, color: '#1976d2' }} align="right">
                                        {row.revenue.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={tdSx} align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                                            {row.vsRevUp
                                                ? <TrendingUpIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                                                : <TrendingDownIcon sx={{ fontSize: 13, color: '#d32f2f' }} />}
                                            <Typography variant="caption" sx={{ color: row.vsRevUp ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
                                                {row.vsRevUp ? '▲' : '▼'} {row.vsRevPrev}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...tdSx, color: row.growthUp ? '#2e7d32' : '#d32f2f', fontWeight: 600 }} align="right">
                                        {row.growthUp ? '' : '-'}{Math.abs(row.growth)}%
                                    </TableCell>
                                    <TableCell sx={tdSx} align="right">{row.share}%</TableCell>
                                    <TableCell sx={tdSx} align="right">{row.avgRevPerTx.toFixed(2)}</TableCell>
                                    <TableCell sx={tdSx} align="center">{statusChip(row.status)}</TableCell>
                                </TableRow>
                            ))}

                            {/* Total / Average row */}
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ ...tdSx, fontWeight: 700 }}>Total / Average</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">2,356,780</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#2e7d32' }} align="right">▲ 9.7%</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">124,560,850</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#2e7d32' }} align="right">▲ 12.5%</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#2e7d32' }} align="right">9.7%</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">100.0%</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">52.88</TableCell>
                                <TableCell sx={tdSx} align="center">–</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* ══════════════════════════════════════════
                    TABLE 2 — Channel Performance Details
                ══════════════════════════════════════════ */}
                <Box sx={{ width: '100%', mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Channel Performance Details</Typography>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                <TableCell sx={thSx}>Date</TableCell>
                                <TableCell sx={thSx} align="right">Cash (Onboard) Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">Online Booking Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">Online Onboard Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">Travel Card Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">Season Tickets Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">Govt Pass Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">Total Revenue (Rs.)</TableCell>
                                <TableCell sx={thSx} align="right">Total Transactions</TableCell>
                                <TableCell sx={thSx} align="right">Non-Cash Revenue %</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detailRows
                                .slice(startIndex, startIndex + rowsPerPage)
                                .map((row, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell sx={tdSx}>{row.date}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.cash.toLocaleString()}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.online.toLocaleString()}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.onboard.toLocaleString()}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.travel.toLocaleString()}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.season.toLocaleString()}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.govt.toLocaleString()}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontWeight: 600, color: '#1976d2' }} align="right">{row.totalRev.toLocaleString()}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.totalTx.toLocaleString()}</TableCell>
                                        <TableCell sx={tdSx} align="right">{row.nonCash}%</TableCell>
                                    </TableRow>
                                ))}

                            {/* MTD Total row */}
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ ...tdSx, fontWeight: 700 }}>MTD Total</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.cash.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.online.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.onboard.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.travel.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.season.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.govt.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.totalRev.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#1976d2' }} align="right">{detailTotals.totalTx.toLocaleString()}</TableCell>
                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: '#2e7d32' }} align="right">75.4%</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    <TablePagination
                        showFirstButton
                        showLastButton
                        component="div"
                        count={detailRows.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[7, 10, 25, 50]}
                    />
                </TableContainer>

            </Box>
        </Container>
    );
};

export default ChannelPerformancePage;